import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, Table, TableBody, TableCell, TableHead, TableRow, TableContainer } from '@mui/material';
import StateTransitionComponent from './StateTransitionComponent';
import PopperComponent from './PopperComponent';
import { Search, Close } from '@mui/icons-material';
import documentTypeIds from 'constants/documentTypeIds';
import { Card, CardHeader, IconButton, TextField } from '@totalsoft/rocket-ui';

const StateTransitionListComponent = ({ stateTransitions, onFilterValueChanged, fullTextFilter, resetFilters, documentTypeId }) => {
  const { t } = useTranslation();

  const [popperState, setPopperState] = useState({
    anchorEl: null,
    open: false,
    content: null
  });

  const handleClick = ct => event => {
    const { currentTarget } = event;
    setPopperState({
      anchorEl: currentTarget,
      open: !popperState.open,
      content: ct
    });
  };

  const handleClose = () => {
    setPopperState({ open: false });
  };

  const openDocumentImage = useCallback(
    item => () => {
      switch (documentTypeId) {
        case documentTypeIds.Offer:
        case documentTypeIds.OfferModification:
        case documentTypeIds.FacilityFrameOffer:
          window.open(`/offers/${item.imageId}/${item.imageSiteId}`, '_blank');
          break;
        case documentTypeIds.Folder:
        case documentTypeIds.FacilityApproval:
          window.open(`/folders/${item.imageId}/${item.imageSiteId}`, '_blank');
          break;
        case documentTypeIds.PartnerFile:
          window.open(`/partner-files/${item.imageId}/${item.imageSiteId}`, '_blank');
          break;
        default:
          break;
      }
    },
    [documentTypeId]
  );

  const id = popperState.open ? 'simple-popper' : null;
  return (
    <Card>
      <CardHeader title={t('Document.StateTransition.Title')}></CardHeader>
      <Grid
        container
        spacing={2}
        sx={{
          padding: '24px'
        }}
      >
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <TextField
            id={'documentVerificationList_filters_fullText'}
            fullWidth={true}
            name='fullTextFilter'
            value={fullTextFilter}
            onChange={e => onFilterValueChanged(e, true)}
            placeholder={t('Placeholders.StateTransitionSearch')}
            startAdornment={<Search />}
            endAdornment={
              <IconButton
                tooltip={t('Tooltips.ResetFilters')}
                color='transparent'
                size='tiny'
                onClick={resetFilters}
                id={'documentVerificationList_filters_resetBtn'}
              >
                <Close />
              </IconButton>
            }
          />
        </Grid>
        <TableContainer>
          <Table
            sx={{
              '@media (max-width: 480px)': {
                minWidth: '500px'
              },
              mt: 1
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>{t('Document.StateTransition.State')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Document.StateTransition.Comments')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Document.StateTransition.Reasons')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Document.StateTransition.Date')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Document.StateTransition.User')}</strong>
                </TableCell>
                <TableCell>
                  <strong>{t('Document.StateTransition.Version')}</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stateTransitions.map(item => {
                return (
                  <StateTransitionComponent
                    item={item}
                    key={item.stateTransitionId}
                    handleClick={handleClick}
                    openDocumentImage={openDocumentImage}
                  />
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <PopperComponent
          content={popperState.content}
          anchorEl={popperState.anchorEl}
          open={popperState.open}
          id={id}
          handleClose={handleClose}
        />
      </Grid>
    </Card>
  );
};

StateTransitionListComponent.propTypes = {
  stateTransitions: PropTypes.array.isRequired,
  resetFilters: PropTypes.func.isRequired,
  fullTextFilter: PropTypes.string,
  onFilterValueChanged: PropTypes.func.isRequired,
  offerTypeId: PropTypes.number,
  documentTypeId: PropTypes.number
};

export default StateTransitionListComponent;
